import type { CityConfig } from "@/pages/city-template/types";

export const danburyConfig: CityConfig = {
  city: "Danbury",
  state: "Connecticut",
  stateAbbr: "CT",
  slug: "danbury",
  route: "/cities/danbury",
  title: "Expedited Passport Services in Danbury, CT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Danbury, CT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Danbury, Bethel, Brookfield, and all of Western Connecticut.",
  h1: "Expedited Passport Services in Danbury, CT",
  heroSubtitle:
    "Danbury travelers rely on Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No need to visit the New York Passport Agency.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Golden%20hour%20aerial%20view%20of%20Danbury%20Connecticut%20downtown%20with%20historic%20buildings%20and%20the%20Still%20River%2C%20western%20Connecticut%20cityscape%2C%20warm%20amber%20lighting%2C%20rolling%20hills%20in%20the%20background%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=ct-db-hero-001&orientation=landscape",
  heroImageAlt: "Downtown Danbury Connecticut at golden hour",
  geoPosition: "41.3948; -73.4540",
  geoPlacename: "Danbury, Connecticut, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~65 miles from Danbury",
  },
  nearbyCitiesZips: [
    { name: "Danbury", zips: "06810, 06811, 06813, 06814, 06816, 06817" },
    { name: "Bethel", zips: "06801" },
    { name: "Brookfield", zips: "06804" },
    { name: "New Fairfield", zips: "06812" },
    { name: "Ridgefield", zips: "06877, 06879" },
    { name: "New Milford", zips: "06776, 06755" },
    { name: "Newtown", zips: "06470, 06482" },
    { name: "Redding", zips: "06875, 06896" },
    { name: "Weston", zips: "06883" },
    { name: "Wilton", zips: "06897" },
    { name: "Shelton", zips: "06484, 06485" },
    { name: "Monroe", zips: "06468" },
    { name: "Easton", zips: "06612" },
    { name: "Trumbull", zips: "06611" },
    { name: "Stratford", zips: "06614, 06615" },
    { name: "Bridgeport", zips: "06601, 06602, 06604, 06605, 06606, 06607, 06608, 06610, 06650, 06673, 06699" },
    { name: "Norwalk", zips: "06850, 06851, 06852, 06853, 06854, 06855, 06856, 06857, 06859, 06860" },
    { name: "Stamford", zips: "06901, 06902, 06903, 06904, 06905, 06906, 06907, 06910, 06911, 06912, 06914, 06920, 06921, 06925, 06926, 06927" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Danbury travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Western Connecticut",
      desc: "Danbury, Bethel, Brookfield, New Fairfield, Ridgefield, and all surrounding Western Connecticut communities.",
    },
  ],
  testimonials: [
    {
      name: "Michael T.",
      location: "Danbury, CT",
      rating: 5,
      text: "I had a family emergency overseas and needed my passport in 2 days. Fast Passport Center delivered it in 24 hours. I can't thank their Danbury team enough.",
      service: "Emergency Passport",
    },
    {
      name: "Sarah H.",
      location: "Bethel, CT",
      rating: 5,
      text: "Renewed my passport through Fast Passport Center for a last-minute trip to Ireland. The process was smooth and I had it back in 4 days. Highly recommend for Western Connecticut.",
      service: "Passport Renewal",
    },
    {
      name: "James R.",
      location: "Ridgefield, CT",
      rating: 5,
      text: "Applied for my daughter's first passport. Fast Passport Center walked me through the parental consent requirements and made it simple. Great service for Ridgefield families.",
      service: "Child Passport",
    },
    {
      name: "Emily W.",
      location: "Brookfield, CT",
      rating: 5,
      text: "Needed a name change after divorce. Fast Passport Center handled the paperwork and I had my updated passport in under a week. Very professional Western Connecticut service.",
      service: "Name Change",
    },
  ],
  facilities: [
    {
      id: "dan-1",
      name: "Danbury Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "133 Main Street",
      city: "Danbury",
      state: "CT",
      zip: "06810",
      phone: "203-792-8451",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Danbury",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "dan-2",
      name: "Danbury City Clerk",
      type: "City Clerk / Passport Services",
      address: "155 Deer Hill Avenue",
      city: "Danbury",
      state: "CT",
      zip: "06810",
      phone: "203-797-4500",
      hours: "Mon-Fri 8:30 AM - 4:30 PM",
      distance: "Danbury City Center",
      website: "https://www.danbury-ct.gov/",
      websiteLabel: "City of Danbury",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "dan-3",
      name: "Bethel Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "5 Center Street",
      city: "Bethel",
      state: "CT",
      zip: "06801",
      phone: "203-743-6439",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Bethel",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "dan-4",
      name: "Ridgefield Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "1 Catoonah Street",
      city: "Ridgefield",
      state: "CT",
      zip: "06877",
      phone: "203-438-4821",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Ridgefield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "dan-5",
      name: "New Milford Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "10 Main Street",
      city: "New Milford",
      state: "CT",
      zip: "06776",
      phone: "860-355-2191",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "New Milford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "dan-6",
      name: "Newtown Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "1 Church Hill Road",
      city: "Newtown",
      state: "CT",
      zip: "06470",
      phone: "203-426-7021",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Newtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Danbury, CT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the New York Passport Agency if I live in Danbury?",
      a: "No — if you use Fast Passport Center, you don't need to visit the New York Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Danbury?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Danbury, Bethel, and Brookfield. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Danbury?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Danbury?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Danbury?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Danbury. We serve Danbury and all of Western Connecticut through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Danbury for international travel?",
      a: "Westchester County Airport (HPN) is the closest at ~25 miles. LaGuardia Airport (LGA) is ~55 miles, JFK International is ~65 miles, and Bradley International (BDL) is ~65 miles. Many Danbury residents use Westchester or JFK for international flights.",
    },
    {
      q: "How much does expedited passport service cost in Danbury?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Danbury?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Danbury?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Danbury?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Danbury have a local passport agency?",
      a: "No, Danbury does not have a regional passport agency. The nearest regional agency is the New York Passport Agency in Manhattan, approximately 65 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Danbury", address: "various CVS locations in Danbury", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Danbury", address: "various Walgreens locations in Danbury", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Danbury", address: "various UPS Store locations in Danbury", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Danbury", address: "various FedEx Office locations in Danbury", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Danbury", address: "various Walmart locations in Danbury", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "CT DMV — Danbury",
      address: "42 Lake Avenue Extension, Danbury, CT 06811",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: true,
    },
    {
      name: "CT DMV — Norwalk",
      address: "540 Main Avenue, Norwalk, CT 06851",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HPN",
      name: "Westchester County Airport",
      distance: "~25 miles from Danbury",
      address: "240 Airport Road, Purchase, NY 10577",
      note: "Closest airport to Danbury with flights to major hubs including Atlanta, Chicago, and Florida. Perfect for domestic connections.",
      flightUrl: "https://www.westchestergov.com/airport",
      flightLabel: "Westchester County Airport",
      featured: true,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~65 miles from Danbury",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's primary airport serving all of New England. Offers domestic flights and international connections.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: false,
    },
    {
      code: "JFK",
      name: "JFK International Airport",
      distance: "~65 miles from Danbury",
      address: "JFK Access Rd, Queens, NY 11430",
      note: "One of the busiest international airports in the world with flights to virtually every global destination.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Danbury travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Danbury residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Danbury residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Danbury residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Danbury, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Danbury residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Danbury travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Danbury residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Danbury residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Danbury residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Danbury." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Danbury." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Danbury travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Danbury travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Western Connecticut team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Danbury address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Danbury", route: "/cities/danbury" },
    { name: "New Haven", route: "/cities/new-haven" },
    { name: "Stamford", route: "/cities/stamford" },
    { name: "Bridgeport", route: "/cities/bridgeport" },
    { name: "Waterbury", route: "/cities/waterbury" },
    { name: "Greenwich", route: "/cities/greenwich" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Danbury and all of Western Connecticut as a remote courier service. We do not have a physical office in Danbury, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeDistance: "Remote courier service — Danbury, CT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Danbury neighborhoods and surrounding Western Connecticut communities. From downtown Danbury to Bethel, Brookfield to New Fairfield — we serve every Western Connecticut community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Danbury and all surrounding Western Connecticut communities. Whether you're in downtown Danbury or a nearby suburb, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Danbury customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Danbury travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Danbury and Western Connecticut travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Danbury, CT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/connecticut",
  statePageName: "Connecticut Passport Services",
};

export default danburyConfig;